import { useCallback } from 'react'
import { useSelector } from 'react-redux'

import { DeleteOrgGroupContactsApiArg } from '@/__generated/es-api'
import { selectToken } from '@/app/module/auth/store/selectors'
import { getGroupContacts } from '@/app/module/groups/store/actions'
import { createNotification } from '@/app/module/notifications/store/actions'
import { selectOrgId } from '@/app/module/user/store/selectors'
import { esApi, getErrorMessage } from '@/app/store/api'
import { useAppDispatch } from '@/app/store/redux-hooks'
import config from '@/config'

type RemoveFromGroupPayload = {
  groupId: number | string
  contactIds: string[]
  includedGroupIds: string[]
}

export const useRemoveFromGroup = () => {
  const dispatch = useAppDispatch()
  const orgId = useSelector(selectOrgId)
  const token = useSelector(selectToken)
  const [mutate, res] = esApi.endpoints.deleteOrgGroupContacts.useMutation()

  const removeFromGroup = useCallback(
    async ({ groupId, contactIds, includedGroupIds }: RemoveFromGroupPayload) => {
      const body: DeleteOrgGroupContactsApiArg['body'] = {
        contactIds: contactIds.map((id) => Number(id)),
        includedGroupIds: includedGroupIds.map((id) => Number(id)),
      }

      const notificationKey = contactIds.join('-')
      try {
        await mutate({ orgId: orgId.toString(), groupId: groupId.toString(), body }).unwrap()
        dispatch(
          getGroupContacts({
            token,
            orgId,
            groupId,
            query: {
              page: 1,
              type: 'NORMAL',
              filter: {
                name: config.modules.groups.type.name,
                value: groupId,
              },
            },
          }),
        )
        dispatch(
          createNotification({
            [`remove-from-group-${notificationKey}`]: {
              type: 'success',
              message: 'Contacts successfully removed from the selected group',
            },
          }),
        )
        return true
      } catch (error) {
        dispatch(
          createNotification({
            [`remove-from-group-${notificationKey}`]: {
              type: 'error',
              message: getErrorMessage(error, "Contacts weren't removed from selected group. Please try again"),
            },
          }),
        )
        return false
      }
    },
    [orgId, token, dispatch, mutate],
  )

  return [removeFromGroup, res] as const
}
